import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import { zoneService } from "../services/zone.service";

export const fetchZones = createAsyncThunk(
  "zone/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await zoneService.getZones();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch zones");
    }
  },
);

export const fetchZoneById = createAsyncThunk(
  "zone/fetchById",
  async (id, thunkAPI) => {
    try {
      return await zoneService.getZoneById(id);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch zone");
    }
  },
);

export const addZone = createAsyncThunk("zone/add", async (zone, thunkAPI) => {
  try {
    return await zoneService.createZone(zone);
  } catch (err) {
    return thunkAPI.rejectWithValue("Failed to create zone");
  }
});

export const editZone = createAsyncThunk(
  "zone/edit",
  async ({ id, zone }, thunkAPI) => {
    try {
      return await zoneService.updateZone(id, zone);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update zone");
    }
  },
);

export const removeZone = createAsyncThunk(
  "zone/remove",
  async (id, thunkAPI) => {
    try {
      await zoneService.hardDeleteZone(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to delete zone");
    }
  },
);

// --- Waiter assignment thunks ---
export const fetchZoneWaiters = createAsyncThunk(
  "zone/fetchWaiters",
  async (zoneId, thunkAPI) => {
    try {
      const waiters = await zoneService.getZoneWaiters(zoneId);
      return { zoneId, waiters };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch zone waiters");
    }
  },
);

export const assignWaiter = createAsyncThunk(
  "zone/assignWaiter",
  async ({ zoneId, userId }, thunkAPI) => {
    try {
      await zoneService.assignWaiterToZone(zoneId, userId);
      return { zoneId, userId }; // no re-fetch
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to assign waiter");
    }
  },
);

export const removeWaiter = createAsyncThunk(
  "zone/removeWaiter",
  async ({ zoneId, userId }, thunkAPI) => {
    try {
      await zoneService.removeWaiterFromZone(zoneId, userId);
      return { zoneId, userId }; // no re-fetch
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to remove waiter");
    }
  },
);

export const fetchZonesByShop = createAsyncThunk(
  "zone/fetchByShop",
  async (shopId, thunkAPI) => {
    try {
      return await zoneService.getZonesByShop(shopId);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch zones for shop");
    }
  },
);

const zoneSlice = createSlice({
  name: "zone",
  initialState: {
    zones: [],
    zone: null,
    // waitersByZoneId: { [zoneId]: UserSummaryDto[] }
    waitersByZoneId: {},
    loading: false,
    waitersLoading: false,
    error: null,
    showModal: false,
    showDeleteModal: false,
  },
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchZones.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.loading = false;
        state.zones = action.payload;
      })
      .addCase(fetchZones.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchZoneById.fulfilled, (state, action) => {
        state.zone = action.payload;
      })
      .addCase(addZone.pending, (state) => {
        state.loading = true;
      })
      .addCase(addZone.fulfilled, (state, action) => {
        state.zones.push(action.payload);
        state.loading = false;
        state.showModal = false;
      })
      .addCase(editZone.pending, (state) => {
        state.loading = true;
      })
      .addCase(editZone.fulfilled, (state, action) => {
        const editedZone = action.meta.arg;
        state.zones = state.zones.map((zone) => {
          if (zone.zoneId === editedZone.id)
            return { ...editedZone.zone, zoneId: editedZone.id };
          return zone;
        });
        state.loading = false;
        state.showModal = false;
      })
      .addCase(removeZone.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeZone.fulfilled, (state, action) => {
        const deletedZoneId = action.meta.arg;
        state.zones = state.zones.filter((z) => z.zoneId !== deletedZoneId);
        state.loading = false;
        state.showDeleteModal = false;
      })
      // --- Waiter assignment ---
      .addCase(fetchZoneWaiters.pending, (state) => {
        state.waitersLoading = true;
      })
      .addCase(fetchZoneWaiters.fulfilled, (state, action) => {
        const { zoneId, waiters } = action.payload;
        state.waitersByZoneId[zoneId] = waiters;
        state.waitersLoading = false;
      })
      .addCase(fetchZoneWaiters.rejected, (state, action) => {
        state.waitersLoading = false;
        state.error = action.payload;
      })
      .addCase(assignWaiter.pending, (state) => {
        state.waitersLoading = true;
      })
      .addCase(assignWaiter.fulfilled, (state, action) => {
        const { zoneId, waiters } = action.payload;
        state.waitersByZoneId[zoneId] = waiters;
        state.waitersLoading = false;
      })
      .addCase(assignWaiter.rejected, (state, action) => {
        state.waitersLoading = false;
        state.error = action.payload;
      })
      .addCase(removeWaiter.pending, (state) => {
        state.waitersLoading = true;
      })
      .addCase(removeWaiter.fulfilled, (state, action) => {
        const { zoneId, waiters } = action.payload;
        state.waitersByZoneId[zoneId] = waiters;
        state.waitersLoading = false;
      })
      .addCase(removeWaiter.rejected, (state, action) => {
        state.waitersLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchZonesByShop.pending, (state) => {
        state.loading = true;
        state.zones = [];
        state.error = null;
      })
      .addCase(fetchZonesByShop.fulfilled, (state, action) => {
        state.loading = false;
        state.zones = action.payload;
      })
      .addCase(fetchZonesByShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { openModal, closeModal, openDeleteModal, closeDeleteModal } =
  zoneSlice.actions;

// Selectors
export const selectZones = (state) => state.zone.zones;
export const selectZoneById = (id) =>
  createSelector([selectZones], (zones) => zones.find((z) => z.zoneId === id));

export const selectWaitersForZone = (zoneId) =>
  createSelector(
    [(state) => state.zone.waitersByZoneId],
    (waitersByZoneId) => waitersByZoneId[zoneId] || [],
  );

export default zoneSlice.reducer;
